import { useCallback } from "react";
import {
  ZyusetuFieldKeyFragmentFragment,
  ZyusetuFieldKeyType,
  ZyusetuFieldValueAnalyzedPayloadFragmentFragment
} from "@/graphql/graphql.ts";
import { ZyusetuStringField } from "@/components/pages/ZyusetuFields/ZyusetuStringField.tsx";
import { ZyusetuNumberField } from "@/components/pages/ZyusetuFields/ZyusetuNumberField.tsx";
import { ZyusetuStringAutocompleteField } from "@/components/pages/ZyusetuFields/ZyusetuStringAutocompleteField.tsx";
import { ZyusetuBooleanValue } from "@/components/pages/ZyusetuFields/ZyusetuBooleanValue.tsx";
import { ZyusetuDateField } from "@/components/pages/ZyusetuFields/ZyusetuDateField.tsx";
import { ZyusetuDateMonthField } from "@/components/pages/ZyusetuFields/ZyusetuDateMonthField.tsx";
import { ZyusetuSelectField } from "@/components/pages/ZyusetuFields/ZyusetuSelectField.tsx";
import { ZyusetuSelectManyField } from "@/components/pages/ZyusetuFields/ZyusetuSelectManyField.tsx";
import { ZyusetuSelectManyWithCustomInputField } from "@/components/pages/ZyusetuFields/ZyusetuSelectManyWithCustomInputField.tsx";
import { ZyusetuManagementCompanyField } from "@/components/pages/ZyusetuFields/ZyusetuManagementCompanyField.tsx";
import { ZyusetuBankAccountField } from "@/components/pages/ZyusetuFields/ZyusetuBankAccountField.tsx";
import { ZyusetuTenantField } from "@/components/pages/ZyusetuFields/ZyusetuTenantField.tsx";
import { useGetFirstBoundingRegions } from "@/hooks/useGetFirstContent.ts";
import { FormSchema } from "@/components/pages/Zyusetu/ZyusetuEditorSection.tsx";
import { ZyusetuEditType } from "@/types/ZyusetuEditType.ts";
import {ZyusetuPremisesLandRightsField} from "@/components/pages/ZyusetuFields/ZyusetuPremisesLandRightsField.tsx";

export function useZyusetuFieldValueInput(
  onFocus: (
    value?: ZyusetuFieldValueAnalyzedPayloadFragmentFragment | null,
  ) => void,
  updateField: (
    keyId: string,
    keyName: string,
    value: FormSchema[string],
  ) => Promise<void>,
  editType?: ZyusetuEditType,
  isDefaultValueDialog: boolean = false
) {
  const getBoundingRegions = useGetFirstBoundingRegions();

  return useCallback(
    (key: ZyusetuFieldKeyFragmentFragment, isAvailable = true) => {
      const handleOnFocus = () =>
        onFocus(
          key.value?.selectedPayload ??
          getBoundingRegions(key.analyzedPayloads)?.payload,
        );
      switch (key.keyType) {
        case ZyusetuFieldKeyType.String:
          return (
            <ZyusetuStringField
              key={`key-input-${key.id}`}
              fieldKey={key}
              onFocus={handleOnFocus}
              updateField={updateField}
              isAvailable={isAvailable}
            />
          );
        case ZyusetuFieldKeyType.Number:
          return (
            <ZyusetuNumberField
              key={`key-input-${key.id}`}
              fieldKey={key}
              updateField={updateField}
              isAvailable={isAvailable}
              onFocus={handleOnFocus}
            />
          );
        case ZyusetuFieldKeyType.StringAutoComplete:
          return (
            <ZyusetuStringAutocompleteField
              key={`key-input-${key.id}`}
              fieldKey={key}
              onFocus={handleOnFocus}
              updateField={updateField}
              isAvailable={isAvailable}
            />
          );
        case ZyusetuFieldKeyType.Boolean:
          return (
            <ZyusetuBooleanValue
              key={`key-input-${key.id}`}
              fieldKey={key}
              onFocus={handleOnFocus}
              updateField={updateField}
              isAvailable={isAvailable}
            />
          );
        case ZyusetuFieldKeyType.Date:
          return (
            <ZyusetuDateField
              key={`key-input-${key.id}`}
              fieldKey={key}
              onFocus={handleOnFocus}
              updateField={updateField}
              isAvailable={isAvailable}
            />
          );
        case ZyusetuFieldKeyType.DateMonth:
          return (
            <ZyusetuDateMonthField
              key={`key-input-${key.id}`}
              fieldKey={key}
              onFocus={handleOnFocus}
              updateField={updateField}
              isAvailable={isAvailable}
            />
          );
        case ZyusetuFieldKeyType.Select:
          return (
            <ZyusetuSelectField
              key={`key-input-${key.id}`}
              fieldKey={key}
              onFocus={handleOnFocus}
              updateField={updateField}
              isAvailable={isAvailable}
            />
          );

        case ZyusetuFieldKeyType.SelectMany:
          return (
            <ZyusetuSelectManyField
              key={`key-input-${key.id}`}
              fieldKey={key}
              onFocus={handleOnFocus}
              updateField={updateField}
              isAvailable={isAvailable}
            />
          );
        case ZyusetuFieldKeyType.SelectManyWithCustomInput:
          return (
            <ZyusetuSelectManyWithCustomInputField
              key={`key-input-${key.id}`}
              fieldKey={key}
              onFocus={handleOnFocus}
              updateField={updateField}
              isAvailable={isAvailable}
              isDefaultValueDialog={isDefaultValueDialog}
            />
          );
        case ZyusetuFieldKeyType.AutoCompleteManagementCompany:
          return (
            <ZyusetuManagementCompanyField
              key={`key-input-${key.id}`}
              fieldKey={key}
              onFocus={handleOnFocus}
              updateField={updateField}
              isAvailable={isAvailable}
            />
          );

        case ZyusetuFieldKeyType.AutoCompleteBankAccount:
          return (
            <ZyusetuBankAccountField
              key={`key-input-${key.id}`}
              fieldKey={key}
              onFocus={handleOnFocus}
              updateField={updateField}
              isAvailable={isAvailable}
            />
          );
        case ZyusetuFieldKeyType.Tenant:
          return <ZyusetuTenantField
            key={`key-input-${key.id}`}
            fieldKey={key}
            onFocus={handleOnFocus}
            updateField={updateField}
            editType={editType}
            isAvailable={isAvailable}/>
        case ZyusetuFieldKeyType.PremisesLandRights:
          return <ZyusetuPremisesLandRightsField
            key={`key-input-${key.id}`}
            fieldKey={key}
            onFocus={onFocus}
            updateField={updateField}
            editType={editType}
            isAvailable={isAvailable}
          />
      }
    },
    [editType, getBoundingRegions, onFocus, updateField],
  );
}
